/**
 * Shared GST calculation helpers used by both the web app and desktop renderer.
 * Keep logic here; never duplicate it in individual pages.
 */

/**
 * Compute item-level totals.
 * skipTax = true for Bill of Supply lines and RCM lines (supplier charges 0 GST).
 */
export function calcItemTotals(
  item: { qty: number; rate: number; gstRate: number },
  skipTax = false,
): { taxableValue: number; gstAmount: number; total: number } {
  const taxableValue = item.qty * item.rate;
  const gstAmount = skipTax ? 0 : taxableValue * (item.gstRate / 100);
  return { taxableValue, gstAmount, total: taxableValue + gstAmount };
}

/**
 * Compute invoice-level subtotal / taxTotal / total from an item array.
 * Items must already have taxableValue and total computed (e.g. via calcItemTotals).
 */
export function recalcInvoiceTotals(
  items: Array<{ taxableValue: number; total: number }>,
): { subtotal: number; taxTotal: number; total: number } {
  const subtotal = items.reduce((s, i) => s + i.taxableValue, 0);
  const total = items.reduce((s, i) => s + i.total, 0);
  return { subtotal, taxTotal: total - subtotal, total };
}

/**
 * Split an invoice's tax total into CGST+SGST (intra-state) or IGST (inter-state).
 * Used in report generators and invoice print views.
 */
export function gstBreakdown(
  taxType: 'cgst_sgst' | 'igst' | string | null | undefined,
  taxTotal: number,
): { cgst: number; sgst: number; igst: number } {
  const isCgst = taxType === 'cgst_sgst';
  return {
    cgst: isCgst ? taxTotal / 2 : 0,
    sgst: isCgst ? taxTotal / 2 : 0,
    igst: isCgst ? 0 : taxTotal,
  };
}

/**
 * Auto-detect tax type from company and party states.
 * Inter-state → IGST; same state or unknown → CGST+SGST.
 */
export function autoTaxType(
  companyState: string | undefined | null,
  partyState: string | undefined | null,
): 'cgst_sgst' | 'igst' {
  const cs = companyState?.trim().toLowerCase();
  const ps = partyState?.trim().toLowerCase();
  return cs && ps && cs !== ps ? 'igst' : 'cgst_sgst';
}

// ── GSTR-2A parser ───────────────────────────────────────────────────────────
import type { Gstr2aEntry } from '@coreledger/shared-types';

// Re-export so consumers that did `import { Gstr2aEntry } from '@coreledger/business-logic'`
// continue to work without any change.
export type { Gstr2aEntry };

function fpToFinancialYear(fp: string): string {
  const month = parseInt(fp.slice(0, 2), 10) || 1;
  const year  = parseInt(fp.slice(2), 10)   || new Date().getFullYear();
  const start = month >= 4 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export function parseGstr2aJson(data: Record<string, unknown>): Omit<Gstr2aEntry, 'id' | 'matchedPiId'>[] {
  const entries: Omit<Gstr2aEntry, 'id' | 'matchedPiId'>[] = [];
  const financialYear = fpToFinancialYear((data.fp as string) ?? '');
  const importDate = new Date().toISOString().slice(0, 10);

  for (const supplier of ((data.b2b ?? []) as Record<string, unknown>[])) {
    const gstin = (supplier.ctin ?? supplier.gstin ?? '') as string;
    const name = (supplier.cname ?? supplier.name ?? undefined) as string | undefined;
    for (const inv of ((supplier.inv ?? []) as Record<string, unknown>[])) {
      let igst = 0, cgst = 0, sgst = 0, cess = 0, taxable = 0;
      for (const item of ((inv.itms ?? []) as Record<string, unknown>[])) {
        const d = (item.itm_det ?? item) as Record<string, number>;
        taxable += d.txval ?? 0;
        igst    += d.igst ?? d.iamt ?? 0;
        cgst    += d.camt ?? 0;
        sgst    += d.samt ?? 0;
        cess    += d.csamt ?? 0;
      }
      entries.push({
        financialYear, importDate,
        supplierGstin: gstin, supplierName: name,
        invoiceNumber: (inv.inum ?? inv.inv_num ?? '') as string,
        invoiceDate: (inv.idt ?? inv.inv_dt ?? undefined) as string | undefined,
        invoiceType: 'B2B',
        placeOfSupply: (inv.pos ?? undefined) as string | undefined,
        reverseCharge: ((inv.rchrg ?? inv.rc) as string) === 'Y',
        taxableValue: taxable, igst, cgst, sgst, cess,
        itcAvailability: (inv.itc as string | undefined),
      });
    }
  }
  for (const supplier of ((data.cdnr ?? []) as Record<string, unknown>[])) {
    const gstin = (supplier.ctin ?? '') as string;
    const name = (supplier.cname ?? undefined) as string | undefined;
    for (const note of ((supplier.nt ?? []) as Record<string, unknown>[])) {
      let igst = 0, cgst = 0, sgst = 0, cess = 0, taxable = 0;
      for (const item of ((note.itms ?? []) as Record<string, unknown>[])) {
        const d = (item.itm_det ?? item) as Record<string, number>;
        taxable += d.txval ?? 0;
        igst    += d.igst ?? d.iamt ?? 0;
        cgst    += d.camt ?? 0;
        sgst    += d.samt ?? 0;
        cess    += d.csamt ?? 0;
      }
      entries.push({
        financialYear, importDate,
        supplierGstin: gstin, supplierName: name,
        invoiceNumber: (note.nt_num ?? note.ntnum ?? '') as string,
        invoiceDate: (note.nt_dt ?? undefined) as string | undefined,
        invoiceType: 'CDNR',
        placeOfSupply: (note.pos ?? undefined) as string | undefined,
        reverseCharge: false, taxableValue: taxable, igst, cgst, sgst, cess,
      });
    }
  }
  return entries.filter(e => e.invoiceNumber);
}

/**
 * Recompute all item and invoice-level totals for a full Invoice object.
 * Handles BOS (no GST), RCM (buyer pays GST — supplier charges zero), and
 * foreign-currency invoices (adds baseCurrencyTotal).
 *
 * Used by the desktop renderer and can be used by the web app's invoice form
 * to keep calculations consistent across surfaces.
 */
export function computeInvoiceTotals<
  T extends {
    docType?: string;
    isRcm?: boolean;
    currency?: string;
    exchangeRate?: number;
    items: Array<{ qty: number; rate: number; gstRate: number; [key: string]: unknown }>;
  },
>(invoice: T): T & { subtotal: number; taxTotal: number; total: number; baseCurrencyTotal?: number } {
  const skipTax = invoice.docType === 'bill_of_supply' || invoice.isRcm === true;
  const items = invoice.items.map((item) => {
    const qty = Number(item.qty) || 0;
    const rate = Number(item.rate) || 0;
    const gstRate = Number(item.gstRate) || 0;
    const { taxableValue, total } = calcItemTotals({ qty, rate, gstRate }, skipTax);
    return { ...item, qty, rate, taxableValue, gstRate, total };
  });
  const subtotal = items.reduce((s, i) => s + (i.taxableValue as number), 0);
  const total = items.reduce((s, i) => s + (i.total as number), 0);
  const exchangeRate = invoice.exchangeRate ?? 1;
  const baseCurrencyTotal =
    invoice.currency && invoice.currency !== 'INR' ? total * exchangeRate : undefined;
  return { ...invoice, items, subtotal, taxTotal: total - subtotal, total, baseCurrencyTotal };
}

/**
 * Compute net payable after TDS deduction, TCS addition, and advance adjustment.
 * Formula F-2 from TEST_PLAN.md:
 *   TDS Amount  = Subtotal × (TDS Rate / 100)
 *   TCS Amount  = Subtotal × (TCS Rate / 100)
 *   Net Payable = Total − TDS Amount + TCS Amount
 *   Balance Due = Net Payable − Advance Received
 */
export function computeNetPayable(params: {
  subtotal: number;
  total: number;
  tdsRate?: number;
  tcsRate?: number;
  advanceReceived?: number;
}): { tdsAmount: number; tcsAmount: number; netPayable: number; balanceDue: number } {
  const { subtotal, total, tdsRate = 0, tcsRate = 0, advanceReceived = 0 } = params;
  const tdsAmount = subtotal * tdsRate / 100;
  const tcsAmount = subtotal * tcsRate / 100;
  const netPayable = total - tdsAmount + tcsAmount;
  const balanceDue = netPayable - advanceReceived;
  return { tdsAmount, tcsAmount, netPayable, balanceDue };
}
